import { AlertTriangle, Lock } from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { TableRow, TableCell } from "../ui/table";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import type { AccountRole, ManagedUser, ConvertibleAccountType } from "./types";
import {
  ACCOUNT_ROLE_LABELS,
  isUserBanned,
  roleVariants,
  providerVariants,
  getProviderLabel,
} from "./types";
import { UserActionsMenu } from "./UserActionsMenu";
import type { Trial } from "./trials-api";
import {
  ACTIVE_PILL,
  DESTRUCTIVE_PILL,
  NEUTRAL_PILL,
  WARNING_PILL,
} from "./trial-pills";
import { useTrialActions } from "./TrialActionsContext";
import { useSupportLoginActions } from "./SupportLoginActionsContext";

type StatusIcon = "dot" | ComponentType<SVGProps<SVGSVGElement>>;

type StatusPill = {
  label: string;
  className: string;
  icon: StatusIcon;
  subline?: string;
};

const getTrialPill = (banned: boolean, trial?: Trial | null): StatusPill => {
  if (banned)
    return { label: "Banned", className: DESTRUCTIVE_PILL, icon: Lock };
  if (!trial) return { label: "No trial", className: NEUTRAL_PILL, icon: "dot" };

  if (trial.status === "revoked") {
    const daysAgo = Math.max(0, -trial.days_left);
    return {
      label: "Account locked",
      className: DESTRUCTIVE_PILL,
      icon: Lock,
      subline: daysAgo > 0
        ? `Trial expired ${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`
        : "Trial revoked",
    };
  }
  if (trial.status === "expired" || trial.days_left <= 0) {
    const daysAgo = Math.max(0, -trial.days_left);
    return {
      label: "Account locked",
      className: DESTRUCTIVE_PILL,
      icon: Lock,
      subline:
        daysAgo > 0
          ? `Trial ended ${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`
          : "Trial ended",
    };
  }
  if (trial.days_left <= 3) {
    return {
      label: "Ending soon",
      className: WARNING_PILL,
      icon: AlertTriangle,
      subline: `Trial · ${trial.days_left} day${trial.days_left === 1 ? "" : "s"} left`,
    };
  }
  return {
    label: "Active",
    className: ACTIVE_PILL,
    icon: "dot",
    subline: `Trial · ${trial.days_left} days left`,
  };
};

type UserTableRowProps = {
  user: ManagedUser;
  isActionLoading: boolean;
  trial?: Trial | null;
  onEdit: (user: ManagedUser) => void;
  onResetPassword: (user: ManagedUser) => void;
  onRevokeSessions: (user: ManagedUser) => void;
  onAccountRoleChange: (userId: string, role: AccountRole) => void;
  onAccountTypeChange: (userId: string, type: ConvertibleAccountType) => void;
  onBanToggle: (user: ManagedUser) => void;
  onDelete: (user: ManagedUser) => void;
};

export const UserTableRow = ({
  user,
  isActionLoading,
  trial,
  onEdit,
  onResetPassword,
  onRevokeSessions,
  onAccountRoleChange,
  onAccountTypeChange,
  onBanToggle,
  onDelete,
}: UserTableRowProps) => {
  const banned = isUserBanned(user);
  const pill = getTrialPill(banned, trial);
  const trialActions = useTrialActions();
  const supportLoginActions = useSupportLoginActions();

  return (
    <TableRow className={`group ${banned ? "bg-destructive/5" : "hover:bg-muted/40"}`}>
      <TableCell
        className={`sticky left-0 z-10 border-r border-border/60 px-4 py-3 whitespace-normal shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] ${banned ? "bg-destructive/5" : "bg-card group-hover:bg-muted/40"}`}
      >
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-lime-600/10 text-lime-900 font-medium">
              {(user.name || user.email || "U").charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-foreground">
              {user.name || "Unnamed"}
            </span>
            <span className="text-xs text-muted-foreground">{user.email}</span>
          </div>
        </div>
      </TableCell>
      <TableCell className="px-4 py-3">
        {user.username ? (
          <span className="text-sm text-muted-foreground">@{user.username}</span>
        ) : (
          <span className="text-xs text-muted-foreground/50">-</span>
        )}
      </TableCell>
      <TableCell className="px-4 py-3">
        {user.account_role ? (
          <Badge
            variant="outline"
            className={`text-xs ${roleVariants[user.account_role.toLowerCase()] ?? roleVariants.none}`}
          >
            {ACCOUNT_ROLE_LABELS[user.account_role] ?? user.account_role}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground/50">-</span>
        )}
      </TableCell>
      <TableCell className="px-4 py-3">
        {user.account_type ? (
          <Badge
            variant="outline"
            className={`text-xs ${roleVariants[user.account_type] ?? roleVariants.none}`}
          >
            {user.account_type}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground/50">-</span>
        )}
      </TableCell>
      <TableCell className="px-4 py-3">
        {user.account_role === "TEACHER" && (user.coTeacherCount ?? 0) > 0 ? (
          <Badge variant="outline" className={`text-xs ${roleVariants.teacher}`}>
            {user.coTeacherCount}{" "}
            {user.coTeacherCount === 1 ? "Co-teacher" : "Co-teachers"}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground/50">-</span>
        )}
      </TableCell>
      <TableCell className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {user.providers && user.providers.length > 0 ? (
            user.providers.map((provider) => (
              <Badge
                key={provider}
                variant="outline"
                className={`text-xs ${providerVariants[provider] || providerVariants.default}`}
              >
                {getProviderLabel(provider)}
              </Badge>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          )}
        </div>
      </TableCell>
      <TableCell className="px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <Badge className={`gap-1 ${pill.className}`}>
            {pill.icon === "dot" ? (
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
            ) : (
              <pill.icon className="h-3 w-3" strokeWidth={2.5} />
            )}
            {pill.label}
          </Badge>
          {pill.subline ? (
            <span className="text-[11px] text-muted-foreground">
              {pill.subline}
            </span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="px-4 py-3 text-sm text-muted-foreground">
        {user.createdAt
          ? new Date(user.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "-"}
      </TableCell>
      <TableCell className="px-4 py-3 text-right">
        <UserActionsMenu
          user={user}
          isLoading={isActionLoading}
          trial={trial}
          onEdit={onEdit}
          onResetPassword={onResetPassword}
          onRevokeSessions={onRevokeSessions}
          onAccountRoleChange={onAccountRoleChange}
          onAccountTypeChange={onAccountTypeChange}
          onBanToggle={onBanToggle}
          onDelete={onDelete}
          onSupportLogin={supportLoginActions?.onSupportLogin}
          onStartTrial={trialActions?.onStartTrial}
          onManageTrial={trialActions?.onManageTrial}
          onReactivateTrial={trialActions?.onReactivateTrial}
        />
      </TableCell>
    </TableRow>
  );
};
